import React, { useEffect, useState } from "react";
import { IconArrowRight } from "./Icons";
import { CONTACT_ENDPOINT, getRecaptchaToken, loadRecaptchaV3, RECAPTCHA_SITE_KEY } from "../recaptcha";
import "../styles/contact.css";
import "../styles/apply.css";

export type ApplicationField = {
  name: string;
  label: string;
  type: "text" | "tel" | "email" | "select";
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  options?: string[];
  /** Span both columns on wide screens. */
  full?: boolean;
};

type Props = {
  /** Subject tag the team sees, e.g. "Rider application". */
  source: string;
  fields: ApplicationField[];
  /** Field names that map onto the contact API's own fullName/phone/email. */
  nameField: string;
  phoneField?: string;
  emailField?: string;
  submitLabel: string;
  successMessage: string;
};

/**
 * Short application form that posts to the contact API, which emails the
 * team. Everything beyond name/phone/email is folded into the message body,
 * so adding a field never needs a backend change.
 */
const ApplicationForm: React.FC<Props> = ({
  source,
  fields,
  nameField,
  phoneField,
  emailField,
  submitLabel,
  successMessage,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [privacyOk, setPrivacyOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  // Load reCAPTCHA as soon as the form appears, so the first submit works.
  // A failure here is retried on submit, which surfaces the error.
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY) loadRecaptchaV3(RECAPTCHA_SITE_KEY).catch(() => {});
  }, []);

  const set = (name: string, value: string) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const missingRequired = fields.some((f) => f.required && !values[f.name]?.trim());
  const canSubmit = privacyOk && !missingRequired && Boolean(RECAPTCHA_SITE_KEY);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_SITE_KEY);
      const own = new Set([nameField, phoneField, emailField]);
      const message = fields
        .filter((f) => !own.has(f.name) && values[f.name]?.trim())
        .map((f) => `${f.label}: ${values[f.name].trim()}`)
        .join("\n");

      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: values[nameField]?.trim(),
          phone: phoneField ? values[phoneField]?.trim() || undefined : undefined,
          email: emailField ? values[emailField]?.trim() || undefined : undefined,
          message: message || source,
          recaptchaToken,
          recaptchaAction: "contact",
          source,
        }),
      });
      if (!res.ok) throw new Error("Failed to send. Please try again in a moment.");

      setSent(true);
      setValues({});
      setPrivacyOk(false);
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="ct__form">
        <div className="success" role="status">
          {successMessage}
        </div>
      </div>
    );
  }

  return (
    <form className="ct__form" onSubmit={onSubmit}>
      <div className="ap__grid">
        {fields.map((f) => {
          const id = `ap-${f.name}`;
          const common = {
            id,
            name: f.name,
            required: f.required,
            value: values[f.name] || "",
          };
          return (
            <div key={f.name} className={`field${f.full ? " ap__full" : ""}`}>
              <label htmlFor={id}>
                {f.label}
                {f.required ? null : <span className="ap__optional"> (optional)</span>}
              </label>
              {f.type === "select" ? (
                <select {...common} onChange={(e) => set(f.name, e.target.value)}>
                  <option value="">Choose one</option>
                  {f.options?.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  {...common}
                  type={f.type}
                  placeholder={f.placeholder}
                  autoComplete={f.autoComplete}
                  maxLength={120}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>

      <label className="ct__check" htmlFor={`ap-privacy-${source}`}>
        <input
          id={`ap-privacy-${source}`}
          type="checkbox"
          checked={privacyOk}
          onChange={(e) => setPrivacyOk(e.target.checked)}
          required
        />
        <span>
          I agree to the <a href="/privacy">Privacy Statement</a> and to PABS
          contacting me about this application.
        </span>
      </label>

      {!RECAPTCHA_SITE_KEY ? (
        <div className="error" role="alert">
          This form is not configured yet. Please use the <a href="/contact">contact page</a>.
        </div>
      ) : null}

      {error ? (
        <div className="error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="ct__actions">
        <button
          className="btn btn--primary btn--lg"
          type="submit"
          disabled={!canSubmit || submitting}
        >
          {submitting ? "Sending…" : submitLabel}
          {submitting ? null : (
            <IconArrowRight size={18} className="btn__icon btn__icon--arrow" />
          )}
        </button>
      </div>

      <p className="ct__recaptcha">
        Protected by reCAPTCHA — Google{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{" "}
        apply.
      </p>
    </form>
  );
};

export default ApplicationForm;
